import {
  Accessor,
  createSignal,
  For,
  Match,
  onCleanup,
  Setter,
  Show,
  Switch,
} from "solid-js";
import { Product } from "~/server/types/search";
import { useOutsideClick } from "~/utils/hooks/use_outside_click";
import {
  InStore,
  Online,
  OnlineAndInStore,
  SearchIcon,
} from "./search_icons";
import { noResultsFound } from "~/assets/assets";
import HubbleImage from "~/widgets/hubble_image";

export type SearchOverlayProps = {
  isActive: Accessor<boolean>;
  setIsActive: Setter<boolean>;
  searchBrands: Accessor<Product[]>;
  totalBrandsCount: number;
  onSearch: (query: string) => Promise<Product[]>;
  onBrandClick: (product: Product) => void;
};

export function SearchOverlay(props: SearchOverlayProps) {
  const [searchQuery, setSearchQuery] = createSignal("");
  const [searchResults, setSearchResults] = createSignal<Product[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);
  const [noResults, setNoResults] = createSignal(false);
  const [inputRef, setInputRef] = createSignal<HTMLInputElement>();

  const [searchOverlayRef, setSearchOverlayRef] =
    createSignal<HTMLDivElement>();
  const [searchBarRef, setSearchBarRef] = createSignal<HTMLButtonElement>();
  useOutsideClick(
    searchOverlayRef,
    () => {
      props.setIsActive(false);
    },
    searchBarRef
  );

  let searchTimeout: number | undefined;

  const handleSearchInput = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const query = target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!query.trim()) {
      setSearchResults(props.searchBrands());
      setNoResults(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setNoResults(false);

    // Debounce search
    searchTimeout = setTimeout(async () => {
      try {
        const results = await props.onSearch(query);
        setSearchResults(results);
        setNoResults(results.length === 0);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
        setNoResults(true);
      } finally {
        setIsLoading(false);
      }
    }, 300) as unknown as number;
  };

  const handleClear = () => {
    setSearchQuery("");
    setSearchResults(props.searchBrands());
    setNoResults(false);
    setIsLoading(false);
    props.setIsActive(false);
    inputRef()?.blur();
  };

  const handleBrandClick = (product: Product) => {
    props.onBrandClick(product);
    handleClear();
  };

  const handleInputFocus = () => {
    props.setIsActive(true);
    if (!searchQuery() && props.searchBrands().length > 0) {
      setSearchResults(props.searchBrands());
    }
  };

  const getRedemptionInfo = (product: Product) => {
    const types: string[] = [];
    if (product.tags.includes("ONLINE")) types.push("online");
    if (product.tags.includes("OFFLINE")) types.push("in-store");

    if (types.length === 2) {
      return {
        icon: <OnlineAndInStore />,
        text: "Redeem online + in-store",
      };
    } else if (types.includes("in-store")) {
      return {
        icon: <InStore />,
        text: "Redeem in-store",
      };
    } else {
      return {
        icon: <Online />,
        text: "Redeem online",
      };
    }
  };

  onCleanup(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });

  return (
    <div class="relative w-full" onClick={(e) => e.stopPropagation()}>
      {/* Search Input */}
      <div
        class="relative"
        ref={setSearchBarRef}
        onClick={() => {
          inputRef()?.focus();
        }}
      >
        <div
          class="flex h-9 items-center gap-2 rounded-xl border border-stroke-2 bg-background-normal-primary px-3 !transition-none sm:h-[36px] "
          classList={{
            "border-b-none rounded-b-none": props.isActive(),
          }}
        >
          {/* Search Icon */}
          <SearchIcon />

          {/* Input */}
          <input
            ref={setInputRef}
            type="text"
            enterkeyhint="done"
            value={searchQuery()}
            onInput={handleSearchInput}
            onFocus={handleInputFocus}
            placeholder="Brand or category name"
            class="flex-1 border-none bg-transparent !text-[16px] text-para-3-regular text-text-normal-primary outline-none placeholder:text-text-normal-tertiary focus:outline-none"
          />

          {/* Clear Button */}
          <Show when={searchQuery() || props.isActive()}>
            <button
              class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-alpha-neutral-16)]"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M12.8037 6.13672C13.0966 5.844 13.5714 5.84388 13.8643 6.13672C14.1565 6.4296 14.1568 6.90455 13.8643 7.19727L11.0605 10L13.8643 12.8037C14.1564 13.0967 14.1569 13.5716 13.8643 13.8643C13.5716 14.1569 13.0967 14.1564 12.8037 13.8643L10 11.0605L7.19727 13.8643C6.90455 14.1568 6.4296 14.1565 6.13672 13.8643C5.84388 13.5714 5.84399 13.0966 6.13672 12.8037L8.93945 10L6.13672 7.19727C5.84383 6.90437 5.84383 6.42961 6.13672 6.13672C6.42961 5.84384 6.90438 5.84383 7.19727 6.13672L10 8.93945L12.8037 6.13672Z"
                  fill="currentColor"
                  class="text-text-normal-secondary"
                />
              </svg>
            </button>
          </Show>
        </div>
      </div>

      {/* Search Results Overlay */}
      <Show when={props.isActive()}>
        <div
          class="shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.08)] no-scrollbar absolute left-0 top-9 w-full overflow-y-auto rounded-xl rounded-t-none border border-t-0 border-stroke-1 bg-background-normal-primary sm:max-h-[400px]"
          ref={setSearchOverlayRef}
        >
          {/* Results Content */}
          <div class="px-1 py-2">
            <Switch>
              {/* Loading State */}
              <Match when={isLoading()}>
                <div class="px-3 py-1">
                  <For each={[1, 2, 3]}>
                    {() => (
                      <div class="flex animate-pulse items-center gap-3 py-2.5">
                        <div class="h-10 w-10 rounded-full bg-background-normal-secondary" />
                        <div class="flex-1 space-y-2">
                          <div class="h-4 w-32 rounded bg-background-normal-secondary" />
                          <div class="h-3 w-24 rounded bg-background-normal-secondary" />
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </Match>

              {/* No Results */}
              <Match when={noResults()}>
                <div class="flex flex-col items-center justify-center py-4">
                  <HubbleImage
                    src={noResultsFound}
                    alt="No results"
                    class="mb-4 h-16 w-16"
                  />
                  <p class="mb-1 text-para-3-regular text-text-normal-primary">
                    No result for "{searchQuery()}"
                  </p>
                  <p class="text-label-regular text-text-normal-secondary">
                    Please try another keyword
                  </p>
                </div>
              </Match>

              {/* Results List */}
              <Match when={searchResults().length > 0}>
                <div>
                  {/* Brand Count */}
                  <div class="px-3 pb-1">
                    <span class="text-label-regular leading-4 text-text-normal-secondary">
                      {props.totalBrandsCount}+ brands
                    </span>
                  </div>

                  {/* Brand List */}
                  <For each={searchResults().slice(0, 10)}>
                    {(product) => {
                      const redemptionInfo = getRedemptionInfo(product);
                      return (
                        <button
                          class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 !transition-none hover:bg-background-normal-secondary"
                          onClick={() => handleBrandClick(product)}
                        >
                          {/* Brand Logo */}
                          <div class="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-stroke-1 bg-background-normal-secondary">
                            <Show
                              when={product.voucherProduct.sqLogoUrl}
                              fallback={
                                <div class="text-tel-label-12-m flex h-full w-full items-center justify-center text-text-normal-secondary">
                                  {product.voucherProduct.title?.charAt(0) ||
                                    "?"}
                                </div>
                              }
                            >
                              <img
                                src={product.voucherProduct.sqLogoUrl}
                                alt={product.voucherProduct.title}
                                class="h-full w-full object-cover"
                              />
                            </Show>
                          </div>

                          {/* Brand Info */}
                          <div class="flex flex-1 flex-col items-start gap-1">
                            <span class="w-full truncate text-left text-para-3-medium leading-5 text-text-normal-primary">
                              {product.voucherProduct.title}
                            </span>
                            <div class="flex items-center gap-1 stroke-text-normal-secondary">
                              {redemptionInfo.icon}
                              <span class="truncate text-label-regular leading-4 text-text-normal-secondary">
                                {redemptionInfo.text}
                              </span>
                            </div>
                          </div>

                          {/* Discount Badge */}
                          <Show
                            when={
                              product.voucherProduct.discountPercentage &&
                              product.voucherProduct.discountPercentage > 0
                            }
                          >
                            <span class="shrink-0 text-label-medium leading-4 text-success-base">
                              {product.voucherProduct.discountPercentage}% off
                            </span>
                          </Show>

                          {/* Arrow */}
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="shrink-0"
                          >
                            <path
                              d="M7.5 15L12.5 10L7.5 5"
                              stroke="currentColor"
                              stroke-width="1.67"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="text-text-normal-tertiary"
                            />
                          </svg>
                        </button>
                      );
                    }}
                  </For>
                </div>
              </Match>
            </Switch>
          </div>
        </div>
      </Show>
    </div>
  );
}
